import { useEffect, useRef, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router";
import { brandAsset } from "@mashriqain/brand";
import { Icon } from "./components/icons";
import { BtnInner } from "./components/kit";
import { Dock } from "./components/Dock";
import { Sidebar } from "./components/Sidebar";
import { StatusPill } from "./components/Status";
import { api, get, signInURL, SIGNED_OUT, type ApiError, type Me, type Overview } from "./lib/api";
import { useResource } from "./lib/hooks";
import { AccountDetailPage } from "./pages/AccountDetail";
import { AccountsPage } from "./pages/Accounts";
import { AuditPage } from "./pages/Audit";
import { ClientDetailPage, NewClientPage } from "./pages/ClientDetail";
import { ClientsPage } from "./pages/Clients";
import { ConnectPage } from "./pages/Connect";
import { OverviewPage } from "./pages/Overview";
import { SettingsPage } from "./pages/Settings";

const SECTION_LABELS: Record<string, string> = { accounts: "Accounts", portals: "Portals", audit: "Audit log", settings: "Configuration", connect: "Portals" };

/** Where the page sits, for the top bar: the section, then the page in it. */
function crumbs(pathname: string): { label: string; to?: string }[] {
  const [section, rest] = pathname.split("/").filter(Boolean);
  if (!section) return [{ label: "Overview" }];
  const label = SECTION_LABELS[section] ?? "Overview";
  if (section === "connect") return [{ label, to: "/portals" }, { label: "Connect an app" }];
  if (!rest) return [{ label }];
  const page = section === "accounts" ? "Account" : rest === "new" ? "New portal" : "Portal";
  return [{ label, to: `/${section}` }, { label: page }];
}

const COLLAPSED_KEY = "ops.sidebar.collapsed";

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

type Gate = { kind: "checking" } | { kind: "in"; me: Me } | { kind: "out"; reason?: string } | { kind: "error"; message: string };

const SIGN_IN_REASONS: Record<string, string> = {
  not_admin: "That account is not an administrator. The ops panel opens only for administrators.",
  expired: "The sign-in took too long or was started in another browser. Please try again.",
  denied: "The sign-on did not let this account into the ops panel.",
  failed: "The sign-in could not be completed. Please try again.",
};

/**
 * The ops panel. Everything here needs an administrator signed in through
 * id.mashriqain.com; the API checks that on every request.
 */
export function App() {
  const location = useLocation();
  const [gate, setGate] = useState<Gate>({ kind: "checking" });

  useEffect(() => {
    const reason = new URLSearchParams(location.search).get("signin") ?? undefined;
    get<Me>("/me").then(
      (me) => setGate({ kind: "in", me }),
      (e: ApiError) =>
        setGate(e.status === 401 || e.status === 403 ? { kind: "out", reason: e.code === "not_admin" ? "not_admin" : reason } : { kind: "error", message: e.message }),
    );
    const signedOut = () => setGate({ kind: "out", reason: "expired" });
    window.addEventListener(SIGNED_OUT, signedOut);
    return () => window.removeEventListener(SIGNED_OUT, signedOut);
    // Only on first load: later 401s arrive through the SIGNED_OUT event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (gate.kind !== "in") {
    return <SignInScreen gate={gate} next={location.pathname === "/" ? "/" : location.pathname + location.search} />;
  }

  const signOut = async () => {
    try {
      const { end_session_url } = await api<{ end_session_url: string }>("DELETE", "/me");
      // Also sign out at id.mashriqain.com, which returns here.
      window.location.assign(end_session_url);
    } catch {
      setGate({ kind: "out" });
    }
  };

  return <Shell me={gate.me} onSignOut={signOut} />;
}

/** The signed-in panel: a sidebar of sections, a top bar, and the page. */
function Shell({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [open, setOpen] = useState(false);
  const overview = useResource<Overview>("/overview");
  const { reload } = overview;

  // Close the drawer on navigation, and refresh the counts beside the
  // sections, which a change on the last page may have moved.
  const [lastPath, setLastPath] = useState(pathname);
  if (lastPath !== pathname) {
    setLastPath(pathname);
    setOpen(false);
  }
  const firstPath = useRef(true);
  useEffect(() => {
    if (firstPath.current) {
      firstPath.current = false;
      return;
    }
    reload();
  }, [lastPath, reload]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const setFolded = (folded: boolean) => {
    setCollapsed(folded);
    try {
      localStorage.setItem(COLLAPSED_KEY, folded ? "1" : "0");
    } catch {
      // The choice just will not outlast the tab.
    }
  };
  const toggleCollapsed = () => setFolded(!collapsed);

  // The search box is in the sidebar, which may be folded or, on a phone,
  // closed: open it first, then put the cursor there once it is drawn.
  const searchRef = useRef<HTMLInputElement>(null);
  const focusSearch = () => {
    if (window.matchMedia("(max-width: 1023px)").matches) setOpen(true);
    else if (collapsed) setFolded(false);
    requestAnimationFrame(() => searchRef.current?.focus());
  };

  // ⌘K or Ctrl+K, and / outside a field, search; [ folds the sidebar.
  const shortcuts = useRef({ focusSearch, toggleCollapsed });
  useEffect(() => {
    shortcuts.current = { focusSearch, toggleCollapsed };
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      const typing = t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName);
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        shortcuts.current.focusSearch();
      } else if (!typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (e.key === "/") {
          e.preventDefault();
          shortcuts.current.focusSearch();
        } else if (e.key === "[") {
          shortcuts.current.toggleCollapsed();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const trail = crumbs(pathname);

  return (
    <div className="shell" data-collapsed={collapsed || undefined} data-open={open || undefined}>
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      <Sidebar
        me={me}
        overview={overview.data}
        collapsed={collapsed}
        searchRef={searchRef}
        onToggle={toggleCollapsed}
        onClose={() => setOpen(false)}
        onSearch={focusSearch}
        onSignOut={onSignOut}
      />
      <button type="button" className="scrim" aria-label="Close menu" tabIndex={-1} onClick={() => setOpen(false)} />

      <div className="workspace">
        <header className="topbar">
          <Link to="/" className="topbar-mark" aria-label="Mashriqain Ops, overview">
            <img src={brandAsset("mark-ivory.svg")} alt="" width={200} height={200} />
          </Link>
          <nav className="crumbs" aria-label="Breadcrumb">
            <ol role="list">
              <li>
                <Link to="/">Ops</Link>
              </li>
              {trail.map((c, i) => (
                <li key={c.label + i} aria-current={i === trail.length - 1 ? "page" : undefined}>
                  <Icon name="chevron" size={14} />
                  {c.to ? <Link to={c.to}>{c.label}</Link> : <span>{c.label}</span>}
                </li>
              ))}
            </ol>
          </nav>
          <div className="topbar-end">
            <StatusPill />
            <button type="button" className="topbar-btn topbar-search" aria-label="Find an account or a page" onClick={focusSearch}>
              <Icon name="search" />
            </button>
          </div>
        </header>

        <main id="main" className="page">
          <Routes>
            <Route index element={<OverviewPage />} />
            <Route path="accounts" element={<AccountsPage />} />
            <Route path="accounts/:id" element={<AccountDetailPage me={me.account} />} />
            <Route path="portals" element={<ClientsPage />} />
            <Route path="portals/new" element={<NewClientPage />} />
            <Route path="portals/:id" element={<ClientDetailPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="connect" element={<ConnectPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <footer className="page-foot">
          <span>Mashriqain Ops · for administrators only</span>
          <span>Every change here is written to the audit log.</span>
        </footer>
      </div>
      <Dock open={open} onMore={() => setOpen(!open)} />
    </div>
  );
}

function SignInScreen({ gate, next }: { gate: Gate; next: string }) {
  const reason = gate.kind === "out" && gate.reason ? SIGN_IN_REASONS[gate.reason] : undefined;
  return (
    <div className="gate-page">
      <header className="gate-bar">
        <span className="gate-mark" aria-hidden="true">
          <img src={brandAsset("mark-ivory.svg")} alt="" width={200} height={200} />
        </span>
        <span className="gate-bar-title">Mashriqain Ops</span>
        <span className="gate-bar-tag">
          <Icon name="shield" size={14} />
          <span className="gate-bar-text">Administrators only</span>
        </span>
      </header>
      <main id="main" className="gate">
        <div className="gate-hero">
          <img src={brandAsset("logo-horizontal-ivory.svg")} alt="Mashriqain" className="gate-logo" width={979} height={210} />
          {gate.kind === "checking" ? (
            <>
              <h1 className="gate-title">Checking your sign-in…</h1>
              <p className="gate-lede">One moment.</p>
            </>
          ) : gate.kind === "error" ? (
            <>
              <h1 className="gate-title">The panel cannot reach the API.</h1>
              <p className="gate-lede">{gate.message}</p>
            </>
          ) : (
            <>
              <h1 className="gate-title">Sign in to Ops</h1>
              <p className="gate-lede">Accounts, portals and the sign-on for the whole group. It opens only for administrators.</p>
            </>
          )}
        </div>
        <div className="gate-card">
          {gate.kind === "out" && (
            <>
              {reason && <p className="gate-reason">{reason}</p>}
              {/* A full page load: the sign-in happens at id.mashriqain.com. */}
              <a className="btn gate-btn" data-variant="primary" href={signInURL(next)}>
                <BtnInner arrow>Sign in with Mashriqain ID</BtnInner>
              </a>
            </>
          )}
          <p className="gate-foot">
            <Icon name="shield" size={14} />
            Signed in through id.mashriqain.com. Every change is recorded.
          </p>
        </div>
        <p className="gate-note">Mashriqain group</p>
      </main>
    </div>
  );
}
